import React, { useState, useEffect } from 'react';
import { adminApiClient } from '../lib/api';
import { getAssetUrl } from '../lib/utils';
import toast from 'react-hot-toast';
import {
  Globe,
  Palette,
  Mail,
  Search as SearchIcon,
  Layout,
  CreditCard,
  Settings,
  Save,
  RotateCcw,
  Menu,
  Trash2
} from 'lucide-react';

interface WebsiteConfig {
  _id?: string;
  siteName: string;
  siteDescription: string;
  siteKeywords: string[];
  siteUrl: string;
  logo: string;
  favicon: string;
  bannerImage: string;
  contactInfo: {
    email: string;
    phone: string;
    address: {
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
    socialMedia: {
      facebook?: string;
      instagram?: string;
      twitter?: string;
      youtube?: string;
      linkedin?: string;
    };
  };
  seo: {
    metaTitle: string;
    metaDescription: string;
    ogImage: string;
  };
  homepage: {
    heroSection: {
      title: string;
      subtitle: string;
      backgroundImage: string;
      ctaText: string;
      ctaLink: string;
    };
    aboutSection: {
      title: string;
      description: string;
      image: string;
    };
  };
  footer: {
    copyrightText: string;
    quickLinks: Array<{
      title: string;
      url: string;
    }>;
  };
  payment: {
    currency: string;
    supportedCurrencies: string[];
    currencyDisplayStyle?: 'symbol' | 'code';
    stripePublishableKey?: string;
    stripeSecretKey?: string;
    stripeWebhookSecret?: string;
  };
  settings: {
    timezone: string;
    language: string;
    theme: {
      primaryColor: string;
      secondaryColor: string;
      tertiaryColor?: string;
      neutralColor?: string;
      fontFamily?: string;
      accentColor?: string;
      tableHeaderColor?: string;
      sidebarBgColor?: string;
    };
    templeRegistration?: {
      enableCustomDomain?: boolean;
    };
  };
  integrations?: {
    googleMapsApiKey?: string;
  };
}

const WebsiteConfigPage: React.FC = () => {
  const [config, setConfig] = useState<WebsiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // File upload states for branding
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    const family = config?.settings?.theme?.fontFamily;
    if (!family) return;
    const id = `admin-font-${family.toLowerCase().replace(/\s+/g, '-')}`;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, '+')}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [config?.settings?.theme?.fontFamily]);

  const getImageUrl = (imagePath: string) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http')) return imagePath;
    return getAssetUrl(imagePath);
  };

  const handleFileUpload = async (file: File) => {
    try {
      const response = await adminApiClient.uploadFile('website-config', file);
      if (response.success) {
        return response.data.path;
      } else {
        throw new Error(response.message || 'Failed to upload file');
      }
    } catch (error) {
      throw error;
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await adminApiClient.getWebsiteConfig();
      if (response.success && response.data) {
        setConfig(response.data.config);
        try {
          const t = (response.data as any)?.data?.config?.settings?.theme;
          if (t && typeof window !== 'undefined') {
            localStorage.setItem('fd_admin_theme_preview', JSON.stringify(t));
          }
        } catch {
          // noop
        }
      } else {
        toast.error(response.message || 'Failed to fetch config');
      }
    } catch (err: any) {
      toast.error('Failed to fetch config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    const toastId = toast.loading('Saving configuration...');
    try {
      setSaving(true);

      const configToSave = { ...config };

      if (logoFile) {
        const logoPath = await handleFileUpload(logoFile);
        configToSave.logo = logoPath;
        setLogoFile(null);
      }

      if (faviconFile) {
        const faviconPath = await handleFileUpload(faviconFile);
        configToSave.favicon = faviconPath;
        setFaviconFile(null);
      }

      if (bannerFile) {
        const bannerPath = await handleFileUpload(bannerFile);
        configToSave.bannerImage = bannerPath;
        setBannerFile(null);
      }

      const response = await adminApiClient.updateWebsiteConfig(configToSave);
      if (response.success) {
        setConfig(configToSave);
        try {
          if (typeof window !== 'undefined') {
            localStorage.setItem('fd_admin_theme_preview', JSON.stringify(configToSave.settings?.theme || {}));
          }
        } catch {
          // noop
        }
        toast.success('Saved successfully!', { id: toastId });
      } else {
        toast.error('Failed to save', { id: toastId });
      }
    } catch (err: any) {
      toast.error(err.message || 'Error saving', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all configuration to defaults? This cannot be undone.')) return;

    const toastId = toast.loading('Resetting...');
    try {
      setSaving(true);
      const response = await adminApiClient.resetWebsiteConfig();
      if (response.success && response.data) {
        setConfig(response.data.config);
        toast.success('Reset complete', { id: toastId });
      } else {
        toast.error('Failed to reset', { id: toastId });
      }
    } catch (err: any) {
      toast.error('Error resetting', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (path: string, value: any) => {
    if (!config) return;
    const keys = path.split('.');
    const newConfig = { ...config };
    let current: any = newConfig;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) current[keys[i]] = {};
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    setConfig(newConfig);
  };

  if (loading) {
    return <div className="flex h-96 items-center justify-center p-6"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-secondary)]"></div></div>;
  }

  if (!config) return <div className="p-8 text-center text-red-600">Failed to load configuration</div>;

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Globe },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'contact', label: 'Contact', icon: Mail },
    { id: 'seo', label: 'SEO', icon: SearchIcon },
    { id: 'homepage', label: 'Homepage', icon: Layout },
    { id: 'footer', label: 'Footer', icon: Menu },
    { id: 'payment', label: 'Payment', icon: CreditCard },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'appearance', label: 'Appearance', icon: Palette }
  ];

  const theme = {
    primaryColor: config.settings.theme.primaryColor || '#F7941D',
    secondaryColor: config.settings.theme.secondaryColor || '#9C6D3F',
    tertiaryColor: config.settings.theme.tertiaryColor || config.settings.theme.accentColor || '#E36511',
    neutralColor: config.settings.theme.neutralColor || '#FDF7F2',
    fontFamily: config.settings.theme.fontFamily || 'Inter',
    tableHeaderColor: config.settings.theme.tableHeaderColor || '#FFF7ED',
    sidebarBgColor: config.settings.theme.sidebarBgColor || '#FFFFFF',
  };

  const setTheme = (key: string, value: string) => {
    updateConfig(`settings.theme.${key}`, value);
    if (key === 'tertiaryColor') {
      updateConfig('settings.theme.accentColor', value);
    }
    // Apply instantly across admin panel before save.
    try {
      const current = {
        primaryColor: theme.primaryColor,
        secondaryColor: theme.secondaryColor,
        tertiaryColor: theme.tertiaryColor,
        neutralColor: theme.neutralColor,
        fontFamily: theme.fontFamily,
        [key]: value,
      };
      const root = document.documentElement;
      root.style.setProperty('--color-primary', current.primaryColor);
      root.style.setProperty('--color-secondary', current.secondaryColor);
      root.style.setProperty('--color-tertiary', current.tertiaryColor);
      root.style.setProperty('--color-neutral', current.neutralColor);
      const stacks: Record<string, string> = {
        Inter: 'Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        Poppins: 'Poppins, Inter, system-ui, sans-serif',
        Roboto: 'Roboto, Inter, system-ui, sans-serif',
        'Open Sans': '"Open Sans", Inter, system-ui, sans-serif',
        Lato: 'Lato, Inter, system-ui, sans-serif',
        'Playfair Display': '"Playfair Display", Georgia, serif',
      };
      root.style.setProperty('--font-family', stacks[current.fontFamily] || `${current.fontFamily}, sans-serif`);
      root.style.setProperty('--table-header-bg', current.tableHeaderColor);
      root.style.setProperty('--sidebar-bg', current.sidebarBgColor);
      localStorage.setItem('fd_admin_theme_preview', JSON.stringify(current));
    } catch {
      // noop
    }
  };

  const toRgb = (hex: string) => {
    const m = hex.match(/^#?([0-9a-fA-F]{6})$/);
    if (!m) return null;
    const v = m[1];
    return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)] as const;
  };
  const luminance = (hex: string) => {
    const rgb = toRgb(hex);
    if (!rgb) return 0;
    const c = rgb.map((x) => {
      const s = x / 255;
      return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  };
  const contrastRatio = (a: string, b: string) => {
    const l1 = luminance(a);
    const l2 = luminance(b);
    const [maxL, minL] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (maxL + 0.05) / (minL + 0.05);
  };
  const primaryOnNeutralContrast = contrastRatio(theme.primaryColor, theme.neutralColor);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Website Configuration</h1>
          <p className="text-sm text-gray-500">Manage site settings, branding, and global content</p>
        </div>
        <div className="flex gap-3">
          <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm">
            <RotateCcw size={18} /> Reset Defaults
          </button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 transition-colors shadow-sm disabled:opacity-70">
            {saving ? <span className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" /> : <Save size={18} />}
            Save Changes
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col md:flex-row min-h-[600px]">
        {/* Sidebar Tabs */}
        <div className="w-full md:w-64 bg-gray-50 border-r border-gray-200 p-2 flex flex-row md:flex-col overflow-x-auto md:overflow-visible">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors w-full ${activeTab === tab.id ? 'bg-white text-[var(--color-primary)] shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}
            >
              <tab.icon size={18} /> {tab.label}
            </button>
          ))}
        </div>

        {/* Content Area */}
        <div className="flex-1 p-6 md:p-8 overflow-y-auto">
          {activeTab === 'basic' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
                  <input type="text" value={config.siteName} onChange={e => updateConfig('siteName', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site URL</label>
                  <input type="url" value={config.siteUrl} onChange={e => updateConfig('siteUrl', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Description</label>
                  <textarea rows={3} value={config.siteDescription} onChange={e => updateConfig('siteDescription', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'branding' && (
            <div className="space-y-8 max-w-3xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">Branding Assets</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">Logo</label>
                  <div className="border rounded-xl p-4 bg-gray-50 flex flex-col items-center gap-4">
                    <img src={getImageUrl(config.logo) || '/placeholder-logo.png'} alt="" className="h-16 object-contain" />
                    <input type="file" accept="image/*" onChange={e => setLogoFile(e.target.files?.[0] || null)} className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] file:text-[color-mix(in_srgb,var(--color-primary)_80%,black)] hover:file:bg-[color-mix(in_srgb,var(--color-primary)_18%,white)]" />
                  </div>
                </div>
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">Favicon</label>
                  <div className="border rounded-xl p-4 bg-gray-50 flex flex-col items-center gap-4">
                    <img src={getImageUrl(config.favicon) || '/favicon.ico'} alt="" className="h-8 w-8 object-contain" />
                    <input type="file" accept="image/*" onChange={e => setFaviconFile(e.target.files?.[0] || null)} className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] file:text-[color-mix(in_srgb,var(--color-primary)_80%,black)] hover:file:bg-[color-mix(in_srgb,var(--color-primary)_18%,white)]" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">Contact Details</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={config.contactInfo.email} onChange={e => updateConfig('contactInfo.email', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={config.contactInfo.phone} onChange={e => updateConfig('contactInfo.phone', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" value={config.contactInfo.address.street} onChange={e => updateConfig('contactInfo.address.street', e.target.value)} className="w-full px-3 py-2 border rounded-lg mb-2" placeholder="Street" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={config.contactInfo.address.city} onChange={e => updateConfig('contactInfo.address.city', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="City" />
                    <input type="text" value={config.contactInfo.address.state} onChange={e => updateConfig('contactInfo.address.state', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="State" />
                    <input type="text" value={config.contactInfo.address.zipCode} onChange={e => updateConfig('contactInfo.address.zipCode', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Zip" />
                    <input type="text" value={config.contactInfo.address.country} onChange={e => updateConfig('contactInfo.address.country', e.target.value)} className="w-full px-3 py-2 border rounded-lg" placeholder="Country" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'seo' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">SEO Configuration</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                  <input type="text" value={config.seo.metaTitle} onChange={e => updateConfig('seo.metaTitle', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" placeholder="Default page title" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                  <textarea rows={3} value={config.seo.metaDescription} onChange={e => updateConfig('seo.metaDescription', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" placeholder="Default SEO description" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">OG Image URL</label>
                  <input type="text" value={config.seo.ogImage} onChange={e => updateConfig('seo.ogImage', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" placeholder="https://..." />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'homepage' && (
            <div className="space-y-8 max-w-3xl animate-in fade-in">
              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">Hero Section</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input type="text" value={config.homepage.heroSection.title} onChange={e => updateConfig('homepage.heroSection.title', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                    <input type="text" value={config.homepage.heroSection.subtitle} onChange={e => updateConfig('homepage.heroSection.subtitle', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Text</label>
                      <input type="text" value={config.homepage.heroSection.ctaText} onChange={e => updateConfig('homepage.heroSection.ctaText', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">CTA Link</label>
                      <input type="text" value={config.homepage.heroSection.ctaLink} onChange={e => updateConfig('homepage.heroSection.ctaLink', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Background Image</label>
                    <input type="text" value={config.homepage.heroSection.backgroundImage} onChange={e => updateConfig('homepage.heroSection.backgroundImage', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" placeholder="Path to image" />
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <h3 className="text-lg font-semibold border-b pb-2">About Section</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input type="text" value={config.homepage.aboutSection.title} onChange={e => updateConfig('homepage.aboutSection.title', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea rows={4} value={config.homepage.aboutSection.description} onChange={e => updateConfig('homepage.aboutSection.description', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                  <input type="text" value={config.homepage.aboutSection.image} onChange={e => updateConfig('homepage.aboutSection.image', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                </div>
              </section>
            </div>
          )}

          {activeTab === 'footer' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">Footer Settings</h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Copyright Text</label>
                <input type="text" value={config.footer.copyrightText} onChange={e => updateConfig('footer.copyrightText', e.target.value)} className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[color-mix(in_srgb,var(--color-primary)_20%,white)] outline-none" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium text-gray-700">Quick Links</label>
                  <button onClick={() => {
                    const newLinks = [...config.footer.quickLinks, { title: 'New Link', url: '#' }];
                    updateConfig('footer.quickLinks', newLinks);
                  }} className="text-sm text-[var(--color-primary)] hover:opacity-80">
                    + Add Link
                  </button>
                </div>
                <div className="space-y-2">
                  {config.footer.quickLinks.map((link, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={link.title}
                        onChange={e => {
                          const newLinks = [...config.footer.quickLinks];
                          newLinks[index].title = e.target.value;
                          updateConfig('footer.quickLinks', newLinks);
                        }}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        placeholder="Link Title"
                      />
                      <input
                        type="text"
                        value={link.url}
                        onChange={e => {
                          const newLinks = [...config.footer.quickLinks];
                          newLinks[index].url = e.target.value;
                          updateConfig('footer.quickLinks', newLinks);
                        }}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        placeholder="URL"
                      />
                      <button onClick={() => {
                        const newLinks = config.footer.quickLinks.filter((_, i) => i !== index);
                        updateConfig('footer.quickLinks', newLinks);
                      }} className="p-2 text-red-500 hover:bg-red-50 rounded">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                  {config.footer.quickLinks.length === 0 && (
                    <div className="text-center py-4 bg-gray-50 rounded-lg text-gray-400 text-sm">No quick links defined</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">Payment Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Base Currency</label>
                  <select value={config.payment.currency} onChange={e => updateConfig('payment.currency', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none">
                    <option value="USD">USD</option>
                    <option value="INR">INR</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency Display Style</label>
                  <select value={config.payment.currencyDisplayStyle || 'symbol'} onChange={e => updateConfig('payment.currencyDisplayStyle', e.target.value as 'symbol' | 'code')} className="w-full px-3 py-2 border rounded-lg outline-none">
                    <option value="symbol">Symbol ($, ₹)</option>
                    <option value="code">Code (USD, INR)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">How currency appears across the website</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supported Currencies</label>
                  <input
                    type="text"
                    value={config.payment.supportedCurrencies.join(', ')}
                    onChange={e => updateConfig('payment.supportedCurrencies', e.target.value.split(',').map(s => s.trim()))}
                    className="w-full px-3 py-2 border rounded-lg outline-none"
                    placeholder="USD, INR, EUR"
                  />
                  <p className="text-xs text-gray-500 mt-1">Comma separated</p>
                </div>
              </div>

              <div className="mt-8 space-y-6">
                <h4 className="text-md font-medium text-gray-900 border-b pb-2 flex items-center gap-2">
                  <CreditCard size={18} className="text-blue-500" />
                  Stripe API Credentials
                </h4>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Publishable Key</label>
                    <input
                      type="text"
                      value={config.payment.stripePublishableKey || ''}
                      onChange={e => updateConfig('payment.stripePublishableKey', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg outline-none font-mono text-sm"
                      placeholder="pk_test_..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Used on the public frontend for Stripe Elements and checkout.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Secret Key</label>
                    <input
                      type="password"
                      value={config.payment.stripeSecretKey || ''}
                      onChange={e => updateConfig('payment.stripeSecretKey', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg outline-none font-mono text-sm"
                      placeholder="sk_test_..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Used on the server for payment verification. Keep this secret!</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Stripe Webhook Secret</label>
                    <input
                      type="password"
                      value={config.payment.stripeWebhookSecret || ''}
                      onChange={e => updateConfig('payment.stripeWebhookSecret', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg outline-none font-mono text-sm"
                      placeholder="whsec_..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Used to verify authenticity of Stripe webhook events. (e.g. whsec_...)</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6 max-w-3xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">General Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                  <input type="text" value={config.settings.timezone} onChange={e => updateConfig('settings.timezone', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default Language</label>
                  <input type="text" value={config.settings.language} onChange={e => updateConfig('settings.language', e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Google Maps API Key</label>
                  <input
                    type="text"
                    value={config.integrations?.googleMapsApiKey || ''}
                    onChange={e => updateConfig('integrations.googleMapsApiKey', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg outline-none"
                    placeholder="AIza..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Used for address autocomplete and location selection in devotee, priest, and temple modules.</p>
                </div>
              </div>

              {/* Global Temple Registration Domain Settings */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Globe className="w-5 h-5 text-orange-600" />
                        <h4 className="text-base font-semibold text-gray-900">
                          Temple Registration Custom Domain & DNS Option
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 max-w-xl">
                        Global setting to control whether applicants registering a new temple can request a custom domain and configure DNS records during registration.
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0 mt-1">
                      <input
                        type="checkbox"
                        checked={config.settings?.templeRegistration?.enableCustomDomain !== false}
                        onChange={(e) => updateConfig('settings.templeRegistration.enableCustomDomain', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                    </label>
                  </div>

                  <div className="mt-4 p-3 rounded-lg border text-xs flex items-start gap-2.5 bg-gray-50/80">
                    {config.settings?.templeRegistration?.enableCustomDomain !== false ? (
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-800 mr-2">
                          Active (Enabled)
                        </span>
                        <span className="text-gray-700">
                          New temples can enter a custom domain and view DNS setup instructions in the public temple registration form.
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-100 text-amber-800 mr-2">
                          Disabled Globally
                        </span>
                        <span className="text-gray-700">
                          Custom domain option and DNS configuration are hidden from all new temple registrations. Super Admins can still assign domains on behalf of registered temples.
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'appearance' && (
            <div className="space-y-6 max-w-4xl animate-in fade-in">
              <h3 className="text-lg font-semibold border-b pb-2">Appearance Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { label: 'Primary', key: 'primaryColor' },
                  { label: 'Secondary', key: 'secondaryColor' },
                  { label: 'Tertiary', key: 'tertiaryColor' },
                  { label: 'Neutral', key: 'neutralColor' },
                ].map((item) => (
                  <div key={item.key} className="rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{item.label}</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={(theme as any)[item.key]}
                        onChange={(e) => setTheme(item.key, e.target.value)}
                        className="h-10 w-12 rounded border p-0 bg-white cursor-pointer"
                      />
                      <input
                        type="text"
                        value={(theme as any)[item.key]}
                        onChange={(e) => setTheme(item.key, e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                ))}

                {[
                  { label: 'Table Header', key: 'tableHeaderColor' },
                  { label: 'Sidebar Background', key: 'sidebarBgColor' },
                ].map((item) => (
                  <div key={item.key} className="rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{item.label}</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={(theme as any)[item.key]}
                        onChange={(e) => setTheme(item.key, e.target.value)}
                        className="h-10 w-12 rounded border p-0 bg-white cursor-pointer"
                      />
                      <input
                        type="text"
                        value={(theme as any)[item.key]}
                        onChange={(e) => setTheme(item.key, e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                ))}

                <div className="md:col-span-2 rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                  <select
                    value={theme.fontFamily}
                    onChange={(e) => setTheme('fontFamily', e.target.value)}
                    className="w-full md:w-80 px-3 py-2 border rounded-lg"
                  >
                    {['Inter', 'Poppins', 'Roboto', 'Open Sans', 'Lato', 'Playfair Display'].map((font) => (
                      <option key={font} value={font}>{font}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Live Preview</h4>
                <div className="rounded-xl p-4" style={{ background: theme.neutralColor, fontFamily: theme.fontFamily }}>
                  <p className="text-lg mb-3" style={{ color: theme.secondaryColor }}>Dharma Modern</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ background: theme.primaryColor }}>Primary</button>
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ background: theme.secondaryColor }}>Secondary</button>
                    <button className="px-4 py-2 rounded text-white text-sm" style={{ background: theme.tertiaryColor }}>Tertiary</button>
                    <button className="px-4 py-2 rounded border text-sm" style={{ borderColor: theme.secondaryColor, color: theme.secondaryColor }}>Outlined</button>
                  </div>
                  <div className="rounded-lg p-4 border bg-white" style={{ borderColor: `${theme.secondaryColor}33` }}>
                    <p className="font-semibold" style={{ color: theme.secondaryColor }}>Preview Card</p>
                    <div className="mt-3 overflow-hidden rounded border border-gray-200">
                      <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider" style={{ background: theme.tableHeaderColor, color: theme.secondaryColor }}>
                        Table Header Preview
                      </div>
                      <div className="p-3 text-xs text-gray-600 bg-white">
                        Changes update instantly here before you save.
                      </div>
                    </div>
                  </div>
                </div>
                <p className={`mt-3 text-xs ${primaryOnNeutralContrast < 4.5 ? 'text-amber-700' : 'text-emerald-700'}`}>
                  Contrast check (primary on neutral): {primaryOnNeutralContrast.toFixed(2)}:1
                  {primaryOnNeutralContrast < 4.5 ? ' — consider darker primary for WCAG AA text contrast.' : ' — good for standard text.'}
                </p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default WebsiteConfigPage;
